import React, { useState, useEffect, useMemo } from 'react';
import { matchPath } from 'react-router-dom';
import { useGetSkillTemplatesForLessonlyContent } from 'components/Skills/queries/skillTemplates';
import { useGetReadinessSkillsForTaggable } from 'components/Skills/queries/skillTags';
import InfoTooltip from 'components/shared/InfoTooltip';
import Error from 'components/InPersonTraining/shared/Error';
import { usePermissions, useFeatureFlags } from 'hooks';
import Skeleton from 'ui/Skeleton';
import {
  TEXT_VARIANTS,
  HEADING_VARIANTS,
  Text,
  Heading,
} from 'components/ui/Typography';
import { Icon } from 'ui';
import { TYPES, REACT_QUERY_STATUS } from 'constants/config';

const ViewPathAssociatedSkills = ({
  setSelectedSkills,
  setSelectedSkillTags,
  toggleEditing,
}) => {
  const hasSkillsEnabled = useFeatureFlags('readiness-enable-skills-2-0');
  const match = () =>
    matchPath(window.location.pathname, { path: '/paths/:pathId' });
  const pathId = match()?.params?.pathId;
  const { isPermitted: isSkillsAdmin } = usePermissions({
    type: 'skill',
    action: 'skills_admin',
  });

  const isNewPath = pathId === 'new';
  const [variables, setVariables] = useState({
    lessonlyContentId: parseInt(pathId, 10),
    lessonlyContentType: TYPES.PATH,
  });

  useEffect(() => {
    setVariables(prevState => ({
      ...prevState,
      lessonlyContentId: parseInt(pathId, 10),
    }));
  }, [pathId]);

  const { data: skillTemplateData, status } =
    useGetSkillTemplatesForLessonlyContent({
      ...variables,
      skip: hasSkillsEnabled || isNewPath,
    });

  const { data: skillTagData, status: skillTagStatus } =
    useGetReadinessSkillsForTaggable({
      taggableId: parseInt(pathId, 10),
      taggableType: TYPES.PATH,
      skip: !hasSkillsEnabled || isNewPath,
    });

  const skills = useMemo(
    () =>
      skillTemplateData?.skillTemplatesForLessonlyContent?.skillTemplates ?? [],
    [skillTemplateData],
  );
  const skillTags = useMemo(
    () => skillTagData?.readinessSkillsForTaggable ?? [],
    [skillTagData],
  );

  useEffect(() => {
    setSelectedSkills(skills);
  }, [setSelectedSkills, skills]);

  useEffect(() => {
    setSelectedSkillTags(skillTags);
  }, [setSelectedSkillTags, skillTags]);

  if (
    (status === REACT_QUERY_STATUS.LOADING &&
      !hasSkillsEnabled &&
      !isNewPath) ||
    (skillTagStatus === REACT_QUERY_STATUS.LOADING &&
      hasSkillsEnabled &&
      !isNewPath)
  ) {
    return (
      <div className="PathBuilderSidebar-section">
        <div className="mb-16">
          <Heading component="h4" variant={HEADING_VARIANTS.H4}>
            <Skeleton />
          </Heading>
        </div>
        <Text component="p" variant={TEXT_VARIANTS.BODY}>
          <Skeleton width={150} />
        </Text>
        <Text component="p" variant={TEXT_VARIANTS.BODY}>
          <Skeleton width={180} />
        </Text>
        <Text component="p" variant={TEXT_VARIANTS.BODY}>
          <Skeleton width={150} />
        </Text>
      </div>
    );
  }

  if (status === REACT_QUERY_STATUS.ERROR) {
    return (
      <Error message={t('skills.associated_skills.error_loading_skills')} />
    );
  }

  if (isNewPath) {
    return (
      <div className="Placeholder fz-14 c-brand-gray-darker">
        {t('skills.associated_skills.no_skills_associated')}
      </div>
    );
  }

  if (skills?.length > 0 && !hasSkillsEnabled) {
    return skills?.map(skill => (
      <div
        key={skill.id}
        className="d-f fd-r pb-4 pr-4 pt-4 mb-4 fz-14 h-bc-gray-lightest c-brand-gray-darker"
      >
        <div className="">{skill.name}</div>
        {skill.description && (
          <InfoTooltip
            className="c-gray-light ml-4"
            tooltip={skill.description}
          />
        )}
      </div>
    ));
  }

  if (skillTags?.length > 0 && hasSkillsEnabled) {
    return skillTags?.map(skillTag => (
      <div
        key={skillTag.id}
        className="d-f fd-r pb-4 pr-4 pt-4 mb-4 fz-14 h-bc-gray-lightest c-brand-gray-darker"
      >
        <div className="">{skillTag.name}</div>
        {skillTag.description && (
          <InfoTooltip
            className="c-gray-light ml-4"
            tooltip={skillTag.description}
          />
        )}
      </div>
    ));
  }

  if (!isSkillsAdmin) {
    return (
      <div className="Placeholder fz-14 c-brand-gray-darker">
        {t('skills.associated_skills.no_skills_associated')}
      </div>
    );
  }

  if (
    (skillTags?.length === 0 && hasSkillsEnabled) ||
    (skills?.length === 0 && !hasSkillsEnabled)
  ) {
    return (
      <button
        type="button"
        className="u-linkBtn fz-base fw-n"
        onClick={toggleEditing}
      >
        <Icon name="add" className="mr-4" />
        {hasSkillsEnabled ? t('tags.add_tag') : t('skills.general.add_skills')}
      </button>
    );
  }
  return null;
};

export default ViewPathAssociatedSkills;
